import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";

import { isArray } from "lodash";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import CircuitStats from "@/pages/session-detail/components/circuit-stats/circuit-stats";
import CognitiveLoad from "@/pages/session-detail/components/cognitive-load/cognitive-load";
import CognitiveLoadBreakdown from "@/pages/session-detail/components/cognitive-load-breakdown/cognitive-load-breakdown";
import CycleContainer from "@/pages/session-detail/components/cycle-container/cycle-container";
import type { CycleContainerProps } from "@/pages/session-detail/components/cycle-container/cycle-container.types";
import FlightPathXCognitiveLoad from "@/pages/session-detail/components/dual-components/flight-path-x-cognitive-load";
import MultiFlightPathCanvasXAltitudeGrid from "@/pages/session-detail/components/dual-components/multi-flight-path-canvas-x-altitude-grid";
import MultiFlightPathCanvasXEyeTrackingCanvas from "@/pages/session-detail/components/dual-components/multi-flight-path-canvas-x-eye-tracking-canvas";
import EyeTracking from "@/pages/session-detail/components/eye-tracking/eye-tracking";
import InterceptStats from "@/pages/session-detail/components/intercept-stats/intercept-stats";
import ProgressScore from "@/pages/session-detail/components/progress-score/progress-score";
import StatsSummary from "@/pages/session-detail/components/stats-summary/stats-summary";
import Tabs from "@/pages/session-detail/components/tabs/tabs";
import TimelineChart from "@/pages/session-detail/components/timeline-chart/timeline-chart";

import {
    ArrowIconWrapper,
    PopUp,
    PopUpListItem,
    Toggle,
} from "@/components/filters/filters.styles";
import { ChevronDownSquareCornersIcon24, DirectionArrowTopRightIcon24 } from "@/components/icons";
import PageHeader from "@/components/ui/page-header/page-header";

import { DropdownFilterVariant } from "@/types/filters";

import { scrollToTop } from "@/utils/scroll-to-top";

import {
    CognitiveLoadWrapper,
    ComponentsScrollableWrapper,
    ModalContentWrapperForScrollableContent,
    ProgressScoreWrapper,
    RightContentWrapperDiv,
} from "../../modal-contents.styles";

import { ColumnWrapper, RowWrapper } from "./flying-circuits-modal-content.styles";

export interface TabType {
    title: string;
    content: React.ReactNode;
}

interface FlyingCircuitsContentProps {
    scrollableContainerHeight: number;
    logoUrl: string;
    getSessionSubtitleDetails: JSX.Element;
    subDimensionsData: any;
}

//Rendered by ProgressScore - Serves Intercept dashboard and Aviation Circuits dashboard
const FlyingCircuitsModalContent: React.FC<FlyingCircuitsContentProps> = ({
    scrollableContainerHeight,
    logoUrl,
    getSessionSubtitleDetails,
    subDimensionsData,
}) => {
    const {
        isCircuitFilterOpen,
        setIsCircuitFilterOpen,
        circuitFilterOptions,
        selectedCircuit,
        setSelectedCircuit,
        selectedCircuitIndex,
        setSelectedCircuitIndex,
    } = useSessionDetailsContext();

    const clickOutsideRef = useDetectClickOutside({
        onTriggered: () => setIsCircuitFilterOpen(false),
    });

    const hasSubDimensionsData = isArray(subDimensionsData);

    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

    const tabTitles = (hasSubDimensionsData ? subDimensionsData[0]?.tabs : [])?.reduce(
        (acc: any, subDimension: any) => {
            acc = [...acc, subDimension.title];

            return acc;
        },
        [],
    );

    //map each subDimension to selected Dimension
    const mapCircuitIdToSelectedCircuit = useCallback(
        (selectedCircuit: string | null) => {
            return (hasSubDimensionsData ? subDimensionsData : [])?.find((circuit: any) => {
                return circuit?.id === selectedCircuit;
            });
        },
        [selectedCircuit, subDimensionsData],
    );

    const modalCategory = useMemo(() => {
        return mapCircuitIdToSelectedCircuit(selectedCircuit)?.category.toLowerCase() || "";
    }, [selectedCircuit, subDimensionsData]);

    //fetches the data for the selected circuits
    const selectedTabEndpointsData = useMemo(() => {
        return (
            mapCircuitIdToSelectedCircuit(selectedCircuit)?.tabs?.[selectedTabIndex]?.dataset || []
        );
    }, [selectedCircuit, selectedTabIndex, subDimensionsData]);

    //toggle filters button
    const handleFilterButtonToggle = useCallback(
        (event: any) => {
            event.stopPropagation();

            setIsCircuitFilterOpen(!isCircuitFilterOpen);
        },
        [isCircuitFilterOpen],
    );

    //handles filter option click
    const handleFilterOptionClick = (event: any, index: number, option: any) => {
        event.stopPropagation();
        setSelectedCircuitIndex(index);
        setSelectedCircuit(option);
        setIsCircuitFilterOpen(false); //to close filters when option is selected
    };

    //scrolls to top when a new circuit is selected
    useEffect(() => {
        scrollToTop("components-scrollable-wrapper");
    }, [selectedCircuit]);

    //scrolls to the specific parameter that was clicked, inside an circuit
    const handleClickOnCircuitParameter = (clickedStatIndex: number) => {
        const scrollContainer = document.getElementById("components-scrollable-wrapper");

        if (!scrollContainer) {
            return;
        }

        const linearProgressData = () => {
            return selectedTabEndpointsData?.["scoring"]?.datasets?.find((data: any) => {
                return data.code === "linear-progress-score";
            })?.data;
        };

        if (!linearProgressData || !linearProgressData()[clickedStatIndex]) {
            return;
        }

        const statTitle = linearProgressData()[clickedStatIndex].title;
        //"circuit" will have to match id passed to InterceptStats and CircuitStats component
        const statId = `${modalCategory}-${statTitle.toLowerCase().replace(" ", "-")}`;
        const parameterElement = document.getElementById(statId);

        if (parameterElement) {
            const headerOffset = 55; //Tabs header's height

            const elementPosition = parameterElement.offsetTop; //element's distance from the top of the container

            const offsetPosition = elementPosition - headerOffset;

            scrollContainer.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        }
    };

    /* Intercept dashboard */
    const renderInterceptDashboard = (selectedTabIndex: number) => {
        if (selectedTabIndex === 0) {
            return (
                <ColumnWrapper>
                    <RowWrapper>
                        <CognitiveLoadWrapper>
                            <CognitiveLoad
                                isInsideModal
                                subDimensionData={selectedTabEndpointsData?.["custom-charts"]}
                            />
                        </CognitiveLoadWrapper>

                        <ProgressScoreWrapper>
                            <ProgressScore
                                isInsideModal
                                subDimensionsData={selectedTabEndpointsData?.["scoring"]}
                                onStatsClickToScroll={handleClickOnCircuitParameter}
                            />
                        </ProgressScoreWrapper>
                    </RowWrapper>

                    <InterceptStats
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["stats-summary"]}
                        id={`${modalCategory}`} //needs to match id in handleClickOnCircuitParameter()
                    />
                </ColumnWrapper>
            );
        }

        if (selectedTabIndex === 1) {
            return (
                <ColumnWrapper>
                    <RowWrapper>
                        <MultiFlightPathCanvasXAltitudeGrid
                            isInsideModal
                            subDimensionsData={[selectedTabEndpointsData?.["multi-flight-circuit"]]}
                        />
                    </RowWrapper>
                    <RowWrapper>
                        <MultiFlightPathCanvasXEyeTrackingCanvas
                            isInsideModal
                            subDimensionsData={[
                                selectedTabEndpointsData?.["multi-flight-circuit"] ?? undefined, //FlightPathCanvas
                                selectedTabEndpointsData?.["gaze-data"] ?? undefined, //EyeTrackingCanvas
                            ]}
                        />
                    </RowWrapper>
                    <RowWrapper>
                        <TimelineChart
                            isInsideModal
                            subDimensionsData={selectedTabEndpointsData?.["combined-custom-charts"]}
                        />
                    </RowWrapper>
                </ColumnWrapper>
            );
        }
    };

    /* Circuits dashboard */
    const renderCircuitsDashboard = (selectedTabIndex: number) => {
        if (selectedTabIndex === 0) {
            return (
                <ColumnWrapper>
                    <RowWrapper>
                        <CognitiveLoadWrapper>
                            <CognitiveLoad
                                isInsideModal
                                subDimensionData={selectedTabEndpointsData?.["custom-charts"]}
                            />
                        </CognitiveLoadWrapper>

                        <ProgressScoreWrapper>
                            <ProgressScore
                                isInsideModal
                                subDimensionsData={selectedTabEndpointsData?.["scoring"]}
                                onStatsClickToScroll={handleClickOnCircuitParameter}
                            />
                        </ProgressScoreWrapper>
                    </RowWrapper>

                    <StatsSummary
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["combined-custom-top-stats"]}
                        componentDescription="Weather Overview" //used in empty state title
                    />

                    <CircuitStats
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["circuit-stats"]}
                        id={`${modalCategory}`} //needs to match id in handleClickOnCircuitParameter()
                    />
                </ColumnWrapper>
            );
        }

        if (selectedTabIndex === 1) {
            return (
                <ColumnWrapper>
                    <FlightPathXCognitiveLoad
                        key={selectedCircuit}
                        isInsideModal
                        subDimensionsData={[
                            selectedTabEndpointsData?.["combined-custom-charts"] ?? undefined, //CognitiveLoad
                            selectedTabEndpointsData?.["custom-charts"] ?? undefined, //CognitiveLoad
                            selectedTabEndpointsData?.["flight-circuit"] ?? undefined, //FlightPath
                        ]}
                    />

                    <EyeTracking
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["combined-custom-charts"]}
                    />

                    <RowWrapper>
                        <TimelineChart
                            isInsideModal
                            subDimensionsData={selectedTabEndpointsData?.["combined-custom-charts"]}
                        />
                    </RowWrapper>
                </ColumnWrapper>
            );
        }
    };

    /* OTM dashboard */
    const renderOtmDashboard = (selectedTabIndex: number) => {
        if (selectedTabIndex === 0) {
            const cycleContainerData: CycleContainerProps[] =
                selectedTabEndpointsData?.["cycle-container"];

            return (
                <ColumnWrapper>
                    {cycleContainerData && cycleContainerData?.length > 0 ? (
                        cycleContainerData.map(
                            (cycleContainer: CycleContainerProps, index: number) => {
                                const id = cycleContainer?.title?.toLowerCase().replace(" ", "-");

                                return (
                                    <CycleContainer
                                        key={`cycle-container-${index}`}
                                        isInsideModal
                                        subDimensionsData={cycleContainer}
                                        id={`${modalCategory}-${id}`} //needs to match id in handleClickOnCircuitParameter()
                                    />
                                );
                            },
                        )
                    ) : (
                        <CycleContainer
                            isInsideModal
                            subDimensionsData={[]}
                            id={`${modalCategory}`}
                        />
                    )}

                    <CognitiveLoadBreakdown
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["cognitive-load-breakdown"]}
                    />
                </ColumnWrapper>
            );
        }

        if (selectedTabIndex === 1) {
            return (
                <ColumnWrapper>
                    <FlightPathXCognitiveLoad
                        key={selectedCircuit}
                        isInsideModal
                        subDimensionsData={[
                            selectedTabEndpointsData?.["combined-custom-charts"] ?? undefined, //CognitiveLoad
                            selectedTabEndpointsData?.["custom-charts"] ?? undefined, //CognitiveLoad
                            selectedTabEndpointsData?.["flight-circuit"] ?? undefined, //FlightPath
                        ]}
                    />
                    <EyeTracking
                        isInsideModal
                        subDimensionsData={selectedTabEndpointsData?.["combined-custom-charts"]}
                    />

                    <RowWrapper>
                        <TimelineChart
                            isInsideModal
                            subDimensionsData={selectedTabEndpointsData?.["combined-custom-charts"]}
                        />
                    </RowWrapper>
                </ColumnWrapper>
            );
        }
    };

    const resolveModalCategory = useCallback((): any => {
        switch (modalCategory) {
            case "intercept":
                return renderInterceptDashboard(selectedTabIndex);
            case "circuits":
                return renderCircuitsDashboard(selectedTabIndex);
            case "otm":
                return renderOtmDashboard(selectedTabIndex);
            default:
                return <> </>;
        }
    }, [modalCategory, selectedTabIndex, selectedCircuit]);

    const tabContent = tabTitles?.map((title: string) => {
        return {
            title: title,
            content: resolveModalCategory(),
        };
    });

    return (
        <ModalContentWrapperForScrollableContent>
            <PageHeader
                title={selectedCircuit || "Flying Circuit"}
                logoUrl={logoUrl}
                subtitleDetailText={getSessionSubtitleDetails}
                rightContent={
                    <RightContentWrapperDiv>
                        <Toggle
                            ref={clickOutsideRef}
                            isOpen={isCircuitFilterOpen}
                            onClick={handleFilterButtonToggle}
                            variant={DropdownFilterVariant.Primary}
                        >
                            <DirectionArrowTopRightIcon24 />
                            {selectedCircuit}
                            <ArrowIconWrapper isOpen={isCircuitFilterOpen}>
                                <ChevronDownSquareCornersIcon24 />
                            </ArrowIconWrapper>
                        </Toggle>

                        {isCircuitFilterOpen && (
                            <PopUp
                                id="modal-filters-popup-id"
                                variant={DropdownFilterVariant.Primary}
                                popUpWidth="16.125rem"
                            >
                                {circuitFilterOptions?.map((option: any, index: number) => {
                                    return (
                                        <PopUpListItem
                                            variant={DropdownFilterVariant.Primary}
                                            isSelected={selectedCircuitIndex === index}
                                            key={option}
                                            onClick={(event) => {
                                                handleFilterOptionClick(event, index, option);
                                            }}
                                        >
                                            {option}
                                        </PopUpListItem>
                                    );
                                })}
                            </PopUp>
                        )}
                    </RightContentWrapperDiv>
                }
            />

            <ComponentsScrollableWrapper
                //id is needed to calculate the scrollable container's height
                id="components-scrollable-wrapper"
                scrollableContainerHeight={scrollableContainerHeight}
            >
                <Tabs
                    data={tabContent}
                    selectedTabIndex={selectedTabIndex}
                    setSelectedTabIndex={setSelectedTabIndex}
                    stickyHeader
                />
            </ComponentsScrollableWrapper>
        </ModalContentWrapperForScrollableContent>
    );
};

export default FlyingCircuitsModalContent;
